import Image, { StaticImageData } from 'next/legacy/image';

import { BodyLarge, BodyRegular } from '@common/body';
import { H3, H4 } from '@common/headline';
import { Overline } from '@common/overline';

import { ImageWithExplainerMobile } from '../ImageWithExplainer/ImageWithExplainerMobile';

import daisy from '@public/img/medically-supported/daisy.png';
import haroon from '@public/img/medically-supported/haroon.png';
import blob from '@public/img/medically-supported/meet-your-team-blob.png';
import rosie from '@public/img/medically-supported/rosie.png';

const TEAM_MEMBERS = [
  {
    image: haroon,
    altText: 'Clinical pharmacist',
    title: 'Haroon Amanji',
    body: (
      <span>
        Clinical Pharmacist
        <br />
        GPhC Reg. No: 2070534
      </span>
    ),

    supertitle: 'MPharm',
  },
  {
    image: rosie,
    altText: 'Head of health coaching',
    title: 'Rosie Carr',
    body: (
      <span>
        Head of Health Coaching &<br />
        Registered Dietitian
      </span>
    ),
    supertitle: 'MSc, BSc, RD',
  },
  {
    image: daisy,
    altText: 'Health coach',
    title: 'Daisy Estephane',
    body: 'Health Coach & Registered Dietitian',
    supertitle: 'MSc, BSc, RD',
  },
];

type Props = {
  image: StaticImageData;
  altText: string;
  title: string;
  body: string | JSX.Element;
  supertitle: string;
};

const ImageWithExplainerDesktop = ({
  image,
  altText,
  title,
  body,
  supertitle,
}: Props): JSX.Element => (
  <div className="flex w-[312px] flex-col items-center text-start">
    <div className="relative h-[378px] w-[311px] overflow-hidden rounded-[32px]">
      <Image layout="fill" objectFit="cover" src={image} alt={altText} />
    </div>
    {supertitle && (
      <div className="mt-6 w-full text-dawn-blue">
        <Overline>{supertitle}</Overline>
      </div>
    )}
    <div className={`w-full text-mono-white ${supertitle ? 'mt-2' : 'mt-8'}`}>
      <H4>{title}</H4>
    </div>
    {body && (
      <div className="mt-2 w-full text-blue-200">
        <BodyRegular>{body}</BodyRegular>
      </div>
    )}
  </div>
);

export const MeetYourTeam = (): JSX.Element => (
  <div className="relative flex w-full justify-center bg-blue px-3 pb-20 pt-16 text-mono-white lg:h-[916px] lg:px-[60px] lg:py-[108px]">
    <div className="absolute left-0 top-[200px] hidden h-[570px] w-[230px] lg:block">
      <Image
        src={blob}
        alt="blob"
        layout="fill"
        objectFit="contain"
        objectPosition="left center"
      />
    </div>
    <div className="container m-auto flex w-full flex-col items-center">
      <div className="max-w-[872px] text-center">
        <div className="mb-4">
          <H3>
            Meet <span className="text-teal">your team</span>
          </H3>
        </div>
        <div className="mb-[72px] hidden text-blue-200 lg:block">
          <BodyLarge>
            Meet a few of our international team of nutritionists, dietitians,
            and clinicians who will help you throughout your whole journey. You
            are in safe hands.
          </BodyLarge>
        </div>
        <div className="mb-[54px] max-w-[500px] text-blue-200 lg:hidden">
          <BodyRegular>
            Meet a few of our international team of nutritionists, dietitians,
            and clinicians who will help you throughout your whole journey. You
            are in safe hands.
          </BodyRegular>
        </div>
      </div>
      <div className="z-10 flex w-full overflow-auto lg:justify-center">
        <div className="hidden w-[75%] flex-row justify-between gap-x-6 lg:flex">
          {TEAM_MEMBERS.map(({ image, altText, title, body, supertitle }) => (
            <ImageWithExplainerDesktop
              key={`${title}Desktop`}
              image={image}
              altText={altText}
              title={title}
              body={body}
              supertitle={supertitle}
            />
          ))}
        </div>
        <div className="flex gap-x-6 lg:hidden">
          {TEAM_MEMBERS.map(({ image, altText, title, body, supertitle }) => (
            <ImageWithExplainerMobile
              key={`${title}Mobile`}
              image={image}
              altText={altText}
              title={title}
              body={body}
              supertitle={supertitle}
              textAlign="start"
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);
